import { useState, useCallback, useEffect } from "react";
import "../../assets/css/reservation.css";
import { useNavigate } from "react-router-dom";
import { ReservationsController } from "../../controllers/reservationsController";
import { ReservationSlotSettingsController } from "../../controllers/reservationSlotSettingsController";
import { validateReservationForm } from "../../validations/reservationForm";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Dropdown } from "../../components/dropdown";
import { useToastPromise } from "../../components/toastPromise";
import { ApiUtil } from "../../util/apiUtil";
import { Button } from "react-bootstrap";
import { Loading } from "../../components/loading";
import { ReservationSettingsController } from "../../controllers/reservationSettingsController";
import { SlotButtons } from "../../components/slotButtons";
import { DateUtil } from "../../util/dateUtil";
import { CalendarDots } from "../../constants/calendarDots";
import { CollaborationController } from "../../controllers/collaborationController";
import { PopupDialog } from "../../components/popupDialog";
import { MyReservations } from "./myReservations";
import { Status } from "../../constants/status";
import { GeneralController } from "../../controllers/generalController";
import { TableName } from "../../constants/tableName";
import { RoleType } from "../../constants/roleType";
import { ReservationManager } from "./manager/reservationManager";
import { FaCalendarAlt, FaClock, FaUsers } from "react-icons/fa";
import { toast } from "react-toastify";
import { MdOutlineAddCircle } from "react-icons/md";
import { SlotStatus } from "../../constants/slotStatus";

export const Reservation = () => {
  const initialInputs = {
    reservationDate: DateUtil.toYYYYMMDDFormat(new Date()),
    timeSlot: {},
    pax: 0,
    status: Status.ACTIVE,
    addOn: {},
  };
  const toastPromise = useToastPromise();
  const navigate = useNavigate();
  const [user] = useState(() => JSON.parse(localStorage.getItem("user")));
  const [isManager, setIsManager] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reservationSettings, setReservationSettings] = useState({});
  const [newTimeSlots, setNewTimeSlots] = useState([]);
  const [newAddOnSlots, setNewAddOnSlots] = useState([]);
  const [filteredEventSlots, setFilteredEventSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAddOn, setSelectedAddOn] = useState(null);
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [myNormalReservations, setMyNormalReservations] = useState([]);
  const [myEventReservations, setMyEventReservations] = useState([]);
  const [isEventSelection, setIsEventSelection] = useState(false);
  const initialSelectedDate = {
    weekend: null,
    weekday: null,
  };
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [colors] = useState(() => JSON.parse(localStorage.getItem("colors")));
  const [minDate, setMinDate] = useState(new Date());
  const [maxDate, setMaxDate] = useState(new Date());
  const [showReservationDescription, setShowReservationDescription] =
    useState(true);
  const [showNormalReservation, setShowNormalReservation] = useState(false);
  const [showEventReservation, setShowEventReservation] = useState(false);
  const [showPastReservations, setShowPastReservations] = useState(false);
  const [open, setOpen] = useState(false);
  const [isNormalReserved, setIsNormalReserved] = useState(false);
  const [isEventReserved, setIsEventReserved] = useState(false);
  const [isToday, setIsToday] = useState(false);
  const [isTwoDaysAdvance, setIsTwoDaysAdvance] = useState(true);
  const normalReservationDates = myNormalReservations.map((reservation) =>
    reservation.status === Status.ACTIVE
      ? new Date(reservation.reservationDate).toDateString()
      : null
  );
  const eventReservationDates = myEventReservations.map((reservation) =>
    reservation.status === Status.ACTIVE
      ? new Date(reservation.reservationDate).toDateString()
      : null
  );
  const getPaxList = (maxPax) => {
    if (typeof maxPax !== "number") maxPax = Number(maxPax);
    return Array.from({ length: maxPax + 1 }, (_, i) => ({
      value: i,
      label: i.toString(),
    }));
  };
  const eventDates = reservationSettings?.eventSettings?.timeSlotsList
    ? reservationSettings?.eventSettings?.timeSlotsList?.map((slot) =>
        new Date(slot.eventDate).toDateString()
      )
    : [];
  const handleOnChange = useCallback((value, name) => {
    setInputs((prev) => ({ ...prev, [name]: value }));
  }, []);
  const handleOnClick = (type) => async (event, slot) => {
    setIsEventSelection(type === "event");
    setSelectedSlot(slot.slotTitle);
    setInputs((prev) => ({ ...prev, timeSlot: slot }));
    if (isTwoDaysAdvance && slot && slot.id) {
      await checkAddOnAvailabilityForSelectedSlot(slot);
    }
  };
  const handleAddOnOnClick = () => (event, slot) => {
    setSelectedAddOn(slot.slotTitle);
    setInputs((prev) => ({ ...prev, addOn: slot }));
  };
  const handleDate = (dateTime) => {
    setIsToday(dateTime.toDateString() === new Date().toDateString());
    const selectedDateFormatted = DateUtil.toYYYYMMDDFormat(dateTime);
    setInputs((prev) => ({
      ...prev,
      reservationDate: getFilteredEventSlots(
        dateTime,
        reservationSettings?.eventSettings?.timeSlotsList
      ),
    }));
    if (dateTime.getDay() === 0 || dateTime.getDay() === 6) {
      setSelectedDate({ weekend: selectedDateFormatted, weekday: null });
    } else {
      setSelectedDate({ weekday: selectedDateFormatted, weekend: null });
    }
    setSelectedSlot(null);
    setSelectedAddOn(null);
    setNewAddOnSlots([
      {
        slotTitle: "No Add-On",
        slotStatus: SlotStatus.NEW,
      },
    ]);
    setInputs((prev) => ({ ...prev, timeSlot: {}, pax: 0, addOn: {} }));
  };
  const handlePopup = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = validateReservationForm(inputs);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservation();
  };
  const showDiv = ({
    reservationDescription = true,
    normalReservation = false,
    eventReservation = false,
    pastReservations = false,
  }) => {
    setShowReservationDescription(reservationDescription);
    setShowNormalReservation(normalReservation);
    setShowEventReservation(eventReservation);
    setShowPastReservations(pastReservations);
    resetForm();
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setSelectedSlot(null);
    setSelectedDate(initialSelectedDate);
    setOpen(false);
    setNewAddOnSlots([]);
  };
  const saveReservation = (id = null, type = null) => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        id
          ? ReservationsController.cancel({
              type: type
                ? type
                : isEventSelection
                ? TableName.EVENTRESERVATIONS
                : TableName.NORMALRESERVATIONS,
              parameters: {
                id: id,
                status: Status.CANCELLED,
                userId: user.uid,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve,
              reject,
            })
          : ReservationsController.add({
              type: isEventSelection
                ? TableName.EVENTRESERVATIONS
                : TableName.NORMALRESERVATIONS,
              parameters: {
                ...inputs,
                userId: user.uid,
                reservationSettingsId: reservationSettings.id,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve,
              reject,
            });
      }),
      successMessage: (data) => data,
      errorMessage: (data) => data,
      finishPromise: () => {
        getExistingCollaborationId();
        resetForm();
        setLoading(false);
        showDiv({
          reservationDescription: false,
          eventReservation: false,
          normalReservation: false,
          pastReservations: true,
        });
      },
      failPromise: () => {
        setLoading(false);
        !user
          ? navigate("/loginSignUp", { state: { isLoginToggle: true } })
          : resetForm();
      },
    });
  };
  const getFilteredEventSlots = (dateTime, eventTimeSlots = []) => {
    const selectedDateFormatted = DateUtil.toYYYYMMDDFormat(dateTime);
    setFilteredEventSlots(
      eventTimeSlots.filter((data) => data.eventDate === selectedDateFormatted)
    );
    return selectedDateFormatted;
  };
  const getFilteredTodaySlots = async (timeSlots) => {
    const updatedTimeSlots = await checkEachSlotStatus(timeSlots);
    if (inputs.reservationDate !== DateUtil.toYYYYMMDDFormat(new Date()))
      return updatedTimeSlots;
    const now = new Date();
    now.setHours(now.getHours() + 2);
    return updatedTimeSlots.filter(
      (slot) =>
        new Date(`${now.toISOString().split("T")[0]}T${slot.startTime}`) >= now
    );
  };
  const checkEachSlotStatus = (timeSlots) => {
    const getSlotStatus = (timeSlot) =>
      new Promise((resolve, reject) => {
        ReservationSlotSettingsController.getNewSlotStatus({
          type: isEventSelection
            ? TableName.EVENTRESERVATIONSLOTSETTINGS
            : TableName.NORMALRESERVATIONSLOTSETTINGS,
          reservationSettingsId: reservationSettings.id,
          reservationDate: inputs.reservationDate,
          timeSlotsId: timeSlot.id,
          resolve,
          reject,
        });
      }).then((data) =>
        data
          ? { ...timeSlot, slotStatus: data }
          : new Promise((resolve, reject) => {
              ReservationsController.checkEachSlotStatus({
                type: showEventReservation
                  ? TableName.EVENTRESERVATIONS
                  : TableName.NORMALRESERVATIONS,
                reservationDate: inputs.reservationDate,
                timeSlotsId: timeSlot.id,
                maxPax: timeSlot.maxPax,
                slotStatus: timeSlot.slotStatus,
                resolve,
                reject,
              });
            }).then((data) => ({ ...timeSlot, slotStatus: data }))
      );
    return Promise.all(timeSlots.map(getSlotStatus));
  };
  const checkAddOnAvailabilityForSelectedSlot = async (selectedTimeSlot) => {
    try {
      const addOnStatus = await new Promise((resolve, reject) => {
        ReservationsController.checkAddOnSlotStatus({
          type: isEventSelection
            ? TableName.EVENTRESERVATIONS
            : TableName.NORMALRESERVATIONS,
          reservationDate: inputs.reservationDate,
          timeSlotsId: selectedTimeSlot.id,
          resolve,
          reject,
        });
      });
      const updatedAddOnSlots = [
        {
          slotTitle: "Special Training Camp BBQ Set",
          slotStatus: addOnStatus,
        },
        {
          slotTitle: "No Add-On",
          slotStatus: SlotStatus.NEW,
        },
      ];
      setNewAddOnSlots(updatedAddOnSlots);
      if (
        selectedAddOn === "Special Training Camp BBQ Set" &&
        addOnStatus === SlotStatus.FULLYBOOKED
      ) {
        setSelectedAddOn(null);
        setInputs((prev) => ({ ...prev, addOn: {} }));
      }
    } catch (error) {
      console.debug(
        "Error checking add-on availability for selected slot:",
        error
      );
    }
  };

  const checkCanReserve = (type) => {
    return new Promise((resolve, reject) => {
      ReservationsController.checkCanReserve({
        type: type,
        parameters: { userId: user.uid },
        resolve: resolve,
        reject: reject,
      });
    });
  };
  const getExistingCollaborationId = () => {
    new Promise((resolve, reject) => {
      GeneralController.getAll({ resolve: resolve, reject: reject });
    }).then((data) => {
      const fetchedData = ApiUtil.deserialize({ data: data });
      if (fetchedData.length > 0) getAll(fetchedData[0].existingCollaboration);
    });
  };
  const getCollaborationDetails = (existingCollaboration) => {
    return new Promise((resolve, reject) => {
      CollaborationController.getAllViaId({
        id: existingCollaboration,
        resolve,
        reject,
      });
    });
  };
  const getAllMyNormalReservations = (reservationSettingsId) => {
    return new Promise((resolve, reject) => {
      ReservationsController.get({
        type: TableName.NORMALRESERVATIONS,
        userId: user?.uid ?? "",
        reservationSettingsId: reservationSettingsId,
        resolve,
        reject,
      });
    });
  };
  const getAllMyEventReservations = (reservationSettingsId) => {
    return new Promise((resolve, reject) => {
      ReservationsController.get({
        type: TableName.EVENTRESERVATIONS,
        userId: user?.uid ?? "",
        reservationSettingsId: reservationSettingsId,
        resolve,
        reject,
      });
    });
  };
  const getAllReservationSettings = (existingCollaboration) => {
    return new Promise((resolve, reject) => {
      ReservationSettingsController.getAllViaCollaborationId({
        collaborationId: existingCollaboration,
        resolve,
        reject,
      });
    });
  };
  const getAll = (existingCollaboration) => {
    Promise.all([
      getCollaborationDetails(existingCollaboration),
      getAllReservationSettings(existingCollaboration),
      checkCanReserve(TableName.NORMALRESERVATIONS),
      checkCanReserve(TableName.EVENTRESERVATIONS),
    ])
      .then(
        ([
          collaborationDetailsData,
          reservationSettingsData,
          isNormalReservedData,
          isEventReservedData,
        ]) => {
          const collab = ApiUtil.deserialize({
            data: collaborationDetailsData,
          });
          setMinDate(new Date(collab.startDate));
          setMaxDate(new Date(collab.endDate));
          const settings = ApiUtil.deserialize({
            data: reservationSettingsData,
          })[0];
          setReservationSettings(settings);
          getFilteredEventSlots(
            new Date(),
            settings.eventSettings?.timeSlotsList
          );
          Promise.all([
            getAllMyNormalReservations(settings.id),
            getAllMyEventReservations(settings.id),
          ]).then(([myNormalReservationsData, myEventReservationsData]) => {
            setMyNormalReservations(
              ApiUtil.deserialize({ data: myNormalReservationsData })
            );
            setMyEventReservations(
              ApiUtil.deserialize({ data: myEventReservationsData })
            );
          });
          setIsNormalReserved(isNormalReservedData);
          setIsEventReserved(isEventReservedData);
          handleReserved(isNormalReservedData && isEventReservedData);
        }
      )
      .catch((error) => console.debug(error))
      .finally(() => setLoading(false));
  };
  const handleReserved = (show) => {
    if (show) {
      toast.info(
        `You have one ${Status.getStatusValue(
          Status.ACTIVE
        )} status in reservation type.\nYou cannot make another new reservation until your reservation status is completed or reschedule.`
      );
      setLoading(false);
      showDiv({
        reservationDescription: false,
        eventReservation: false,
        normalReservation: false,
        pastReservations: true,
      });
    }
  };
  useEffect(() => {
    getExistingCollaborationId();
  }, [colors]);
  useEffect(() => {
    if (!user) navigate("/loginSignUp", { state: { isLoginToggle: true } });
    else
      setIsManager(
        user.role === RoleType.MANAGER || user.role === RoleType.ADMIN
      );
  }, [user]);
  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (
        JSON.stringify(selectedDate) === JSON.stringify(initialSelectedDate)
      ) {
        setNewTimeSlots(null);
        return;
      }
      let slotsToCheck;
      if (showEventReservation) {
        slotsToCheck = filteredEventSlots;
      } else if (
        showNormalReservation &&
        (filteredEventSlots[0]?.eventDate === selectedDate.weekday ||
          filteredEventSlots[0]?.eventDate === selectedDate.weekend)
      ) {
        slotsToCheck = filteredEventSlots[0]?.availableNormalTimeSlots;
      } else {
        slotsToCheck = reservationSettings?.normalSettings?.timeSlotsList;
      }
      if (slotsToCheck) {
        const filteredSlots = await getFilteredTodaySlots(slotsToCheck);
        setNewTimeSlots(filteredSlots);
      }
    };
    fetchTimeSlots();
  }, [
    selectedDate,
    showEventReservation,
    showNormalReservation,
    filteredEventSlots,
    reservationSettings,
  ]);
  useEffect(() => {
    setIsTwoDaysAdvance(
      ReservationsController.isReservationTwoDaysInAdvance(
        selectedDate.weekday || selectedDate.weekend
      )
    );
  }, [selectedDate]);

  return loading ? (
    <Loading />
  ) : isManager ? (
    <ReservationManager minDate={minDate} maxDate={maxDate} />
  ) : (
    <section
      id="reservation"
      style={{
        "--primary-color": colors.primaryColor || "#ffffff",
        "--secondary-color": colors.secondaryColor || "#000000",
        "--tertiary-color": colors.tertiaryColor || "#ffffff",
      }}
    >
      <div className="button-container">
        <Button onClick={() => navigate("/")}>Back To Home Page</Button>
        {showPastReservations ? (
          <Button
            onClick={() =>
              showDiv({
                reservationDescription: true,
                normalReservation: false,
                eventReservation: false,
                pastReservations: false,
              })
            }
            className="arrow-button"
          >
            <span className="arrow">{">"}</span>RESERVATION DESCRIPTION
            <span className="arrow">{"<"}</span>
          </Button>
        ) : myNormalReservations.length > 0 ||
          myEventReservations.length > 0 ? (
          <Button
            onClick={() =>
              showDiv({
                reservationDescription: false,
                normalReservation: false,
                eventReservation: false,
                pastReservations: true,
              })
            }
            className="arrow-button"
          >
            <span className="arrow">{">"}</span>MY RESERVATIONS
            <span className="arrow">{"<"}</span>
          </Button>
        ) : (
          <Button
            onClick={() => {}}
            className="arrow-button arrow-button-disabled"
          >
            MY RESERVATIONS
          </Button>
        )}
      </div>
      <div className="reservation-container">
        {showReservationDescription && (
          <div>
            <h3>RESERVATION</h3>
            <hr />
            <div className="rich-text-preview-div margin-bottom">
              <div
                className="rich-text-preview-inner-div"
                dangerouslySetInnerHTML={{
                  __html: reservationSettings.generalDescription,
                }}
              />
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="margin-bottom">
          {(showEventReservation || showNormalReservation) && (
            <div>
              <h3>RESERVATION</h3>
              <hr />

              <div className="row-wrapper">
                <img
                  src={
                    "https://firebasestorage.googleapis.com/v0/b/dokokoko-prd.firebasestorage.app/o/images%2Fdokokoko%2Fcafe%2FHaikyu!!_menus%2FHaikyu%20BBQ%20Set.webp?alt=media&token=fcb93a4e-a049-436e-b98c-fac101f83274"
                  }
                  alt="buffet"
                  className="detail-div"
                />
                <div
                  className="calendar-div"
                  style={{ textAlign: "left", paddingLeft: "1%" }}
                >
                  <h3>Special Training Camp BBQ Set</h3>
                  <label>
                    HKU Special Menu T&C:
                    <ol>
                      <li>
                        <span className="mandatory-field">
                          Advance Reservation Required:
                        </span>
                        <br />
                        Orders must be made at least 2 days in advance via our
                        official reservation system.
                      </li>

                      <li>
                        <span className="mandatory-field">
                          Limited Daily Availability:
                        </span>
                        <br />
                        Reservations are accepted on a first-come, first-served
                        basis. While stocks lasts
                      </li>

                      <li>
                        <span className="mandatory-field">
                          Reservation Limit:
                        </span>
                        <br />
                        Each confirmed reservation is entitled to only one
                        serving of the special menu per session, regardless of
                        the number of guests.
                      </li>

                      <li>
                        <span className="mandatory-field">
                          Cancellation policy:
                        </span>
                        <br />
                        Cancellation policy must be made 1 day in advance
                      </li>
                    </ol>
                  </label>
                </div>
              </div>
              <hr />

              <label>
                <span className="mandatory-field">*</span>
                Due to high demand of the reservations, we had change some
                reservations steps for
                <span className="mandatory-field">
                  {` TODAY'S DATE (${DateUtil.toYYYYMMDDFormat(new Date())}) `}
                </span>
                reservations.
                <br />
                <span className="mandatory-field">*</span>
                Reservations for any time slot must be made
                <span className="mandatory-field"> AT LEAST 2 hours </span>
                before the slot's starting time.
              </label>
              <br />
              <br />
              <div className="row-wrapper">
                <div className="calendar-div">
                  <Calendar
                    minDate={new Date(minDate.getTime() - 24 * 60 * 60 * 1000)}
                    maxDate={maxDate}
                    onChange={handleDate}
                    value={inputs.reservationDate}
                    className="custom-calendar margin-bottom"
                    tileClassName={({ date, view }) => {
                      if (view === "month") {
                        const dateFormatted = DateUtil.toYYYYMMDDFormat(date);
                        if (
                          date <
                            new Date(minDate.getTime() - 24 * 60 * 60 * 1000) ||
                          date > maxDate
                        ) {
                          return "unselectable-date";
                        } else if (
                          new Date().getTime() - 24 * 60 * 60 * 1000 >
                          date
                        ) {
                          return "unselectable-date";
                        }
                        if (date.toDateString() === new Date().toDateString())
                          return selectedDate.weekday === dateFormatted ||
                            selectedDate.weekend === dateFormatted
                            ? "selected-today"
                            : "today";
                        if (date.getDay() === 0 || date.getDay() === 6) {
                          return selectedDate.weekend === dateFormatted
                            ? "selected-weekend"
                            : showEventReservation &&
                              !eventDates.includes(date.toDateString())
                            ? "weekend unselectable-date"
                            : "weekend";
                        } else {
                          return selectedDate.weekday === dateFormatted
                            ? "selected-weekday"
                            : showEventReservation &&
                              !eventDates.includes(date.toDateString())
                            ? "weekday unselectable-date"
                            : "weekday";
                        }
                      }
                      return "";
                    }}
                    tileContent={({ date, view }) => {
                      const dateString = date.toDateString();
                      const isNormalReservationDate =
                        normalReservationDates.includes(dateString);
                      const isEventReservationDate =
                        eventReservationDates.includes(dateString);
                      const isEventDate = eventDates.includes(dateString);
                      if (
                        view === "month" &&
                        (isNormalReservationDate ||
                          isEventReservationDate ||
                          isEventDate)
                      ) {
                        let dotColor = CalendarDots.getColorViaKey(
                          CalendarDots.NORMALDATE
                        );
                        if (
                          (isEventReservationDate && isEventDate) ||
                          isNormalReservationDate
                        ) {
                          dotColor = CalendarDots.getColorViaKey(
                            CalendarDots.RESERVEDDATE
                          );
                        } else if (isEventDate) {
                          dotColor = CalendarDots.getColorViaKey(
                            CalendarDots.EVENTDATE
                          );
                        }
                        return (
                          <span
                            className="event-dot"
                            style={{
                              backgroundColor: dotColor,
                            }}
                          ></span>
                        );
                      }
                      return null;
                    }}
                  />
                  {CalendarDots.CalendarDotsList.map((item) => (
                    <div className="row-wrapper vertical-center" key={item.key}>
                      <span
                        className="event-dot event-dot-display"
                        style={{
                          backgroundColor: item.color,
                        }}
                      ></span>
                      {item.value}
                    </div>
                  ))}
                </div>
                {isToday ? (
                  <label>
                    Kindly contact cafe staff for
                    <span className="mandatory-field"> TODAY </span>reservation
                    slots. Please click{" "}
                    <span className="mandatory-field"> CONTACT </span>button
                    below to proceed.
                  </label>
                ) : (
                  <div className="detail-div">
                    {showEventReservation ? (
                      <div>
                        <SlotButtons
                          name="timeSlot"
                          title="Event Slots"
                          timeSlots={newTimeSlots}
                          handleOnClick={handleOnClick("event")}
                          selectedSlot={selectedSlot}
                          errorMsg={errors.timeSlot}
                        />
                      </div>
                    ) : (
                      <SlotButtons
                        name="timeSlot"
                        title="Normal Slots"
                        timeSlots={newTimeSlots}
                        handleOnClick={handleOnClick("normal")}
                        selectedSlot={selectedSlot}
                        errorMsg={errors.timeSlot}
                      />
                    )}
                    <Dropdown
                      name="pax"
                      title="Pax"
                      value={inputs.pax}
                      options={getPaxList(
                        filteredEventSlots.length > 0
                          ? isEventSelection
                            ? reservationSettings?.eventSettings
                                ?.maxPaxSelection
                            : reservationSettings?.normalSettings
                                ?.maxPaxSelection
                          : reservationSettings?.normalSettings?.maxPaxSelection
                      )}
                      isClearable={false}
                      onChangeFunc={handleOnChange}
                      errorMsg={errors.pax}
                    />
                    <SlotButtons
                      name="addOn"
                      title="Add-On"
                      timeSlots={newAddOnSlots}
                      handleOnClick={handleAddOnOnClick()}
                      selectedSlot={selectedAddOn}
                      errorMsg={errors.addOn}
                      emptyMessage="Please selected time slot."
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
        {showPastReservations && (
          <MyReservations
            myNormalReservations={myNormalReservations}
            myEventReservations={myEventReservations}
            saveReservation={saveReservation}
          />
        )}
      </div>
      <div className="button-container stick-bottom">
        {(showNormalReservation || showEventReservation) && (
          <Button
            onClick={() =>
              showDiv({
                reservationDescription: true,
                normalReservation: false,
                eventReservation: false,
                pastReservations: false,
              })
            }
          >
            BACK
          </Button>
        )}
        {!showEventReservation && !showNormalReservation && (
          <Button
            onClick={() =>
              isNormalReserved
                ? handleReserved(isNormalReserved)
                : showDiv({
                    reservationDescription: false,
                    normalReservation: true,
                    eventReservation: false,
                    pastReservations: false,
                  })
            }
            className="arrow-button"
          >
            <span className="arrow">{">"}</span>NORMAL SLOTS
            <span className="arrow">{"<"}</span>
          </Button>
        )}
        {!showEventReservation &&
          !showNormalReservation &&
          (reservationSettings?.eventSettings?.externalURL ? (
            <Button
              onClick={
                reservationSettings?.eventSettings?.externalURL
                  ? () =>
                      window.open(
                        reservationSettings.eventSettings.externalURL,
                        "_blank",
                        "noopener,noreferrer"
                      )
                  : () => {}
              }
              className="arrow-button"
            >
              <span className="arrow">{">"}</span>EVENT SLOTS
              <span className="arrow">{"<"}</span>
            </Button>
          ) : reservationSettings?.eventSettings?.timeSlotsList?.length > 0 ? (
            <Button
              onClick={() =>
                isEventReserved
                  ? handleReserved(isEventReserved)
                  : showDiv({
                      reservationDescription: false,
                      normalReservation: false,
                      eventReservation: true,
                      pastReservations: false,
                    })
              }
              className="arrow-button"
            >
              <span className="arrow">{">"}</span>EVENT SLOTS
              <span className="arrow">{"<"}</span>
            </Button>
          ) : null)}
        {isToday ? (
          <Button
            onClick={() =>
              window.open(
                "https://wa.me/601168688010",
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="arrow-button"
          >
            <span className="arrow">{">"}</span>CONTACT
            <span className="arrow">{"<"}</span>
          </Button>
        ) : (
          (showEventReservation || showNormalReservation) &&
          (JSON.stringify({}) === JSON.stringify(inputs.timeSlot) ||
          JSON.stringify({}) === JSON.stringify(inputs.addOn) ||
          inputs.pax === 0 ? (
            <Button
              onClick={() => {}}
              className="arrow-button arrow-button-disabled"
            >
              RESERVE
            </Button>
          ) : loading ? (
            <Button
              onClick={handlePopup}
              className="arrow-button arrow-button-disabled"
            >
              <span className="arrow">{">"}</span>RESERVING
              <span className="arrow">{"<"}</span>
            </Button>
          ) : (
            <Button onClick={handlePopup} className="arrow-button">
              <span className="arrow">{">"}</span>RESERVE
              <span className="arrow">{"<"}</span>
            </Button>
          ))
        )}
      </div>
      <PopupDialog
        name="confirmationPopup"
        open={open}
        onClose={handleClose}
        title="Reservation Confirmation"
        content={
          <div>
            <div className="reservation-card">
              <div className="card-header">
                <h4>{inputs?.timeSlot?.slotTitle}</h4>
              </div>
              <div className="card-body">
                <p>
                  <FaCalendarAlt />
                  {`Reservation Date: ${inputs?.reservationDate}`}
                </p>
                <p>
                  <FaClock />
                  {`Time Slot: ${inputs?.timeSlot?.startTime} - ${inputs?.timeSlot?.endTime}`}
                </p>
                <p>
                  <FaUsers /> {`Pax: ${inputs?.pax}`}
                </p>
                <p>
                  <MdOutlineAddCircle />{" "}
                  {`Add-On: ${inputs?.addOn?.slotTitle ?? "No Add-On"}`}
                </p>
              </div>
            </div>
            <br />
            <div className="rich-text-preview-div margin-bottom">
              <div
                className="rich-text-preview-inner-div"
                dangerouslySetInnerHTML={{
                  __html: showEventReservation
                    ? reservationSettings?.eventSettings?.reservationDescription
                    : reservationSettings?.normalSettings
                        ?.reservationDescription,
                }}
              />
            </div>
          </div>
        }
        positiveAction={
          loading ? (
            <Button onClick={loading ? () => {} : handleSubmit} disabled={true}>
              RESERVING
            </Button>
          ) : (
            <Button onClick={handleSubmit}>RESERVE</Button>
          )
        }
        negativeAction={<Button onClick={handleClose}>CLOSE</Button>}
      />
    </section>
  );
};
